import { useState } from "react";

import { isAxiosError } from "axios";

import {
  FEATURE_GATE_METADATA,
  getFeatureGateTitle,
} from "src/adminPanel/featureGateMetadata";
import { usePatchWorkspace } from "src/adminPanel/queries";
import { useCreateOperation, useModules } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { Checkbox } from "src/base-components/Checkbox";
import { Label } from "src/base-components/Label";
import {
  ModuleKind,
  WorkspaceDataplane,
  WorkspaceFeatureGates,
} from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedWs: WorkspaceDataplane;
};

// Agent Builder is no longer a workspace feature gate. It lives on the agents module
// config `feature_flag_tktl_agents` boolean and is changed via the modules API, which
// redeploys the module. This modal still surfaces it as a checkbox, but maps the
// toggle to that module-config change rather than the workspace feature_gates.

export const ModifyWsFeatureGatesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedWs,
}) => {
  const [featureGates, setFeatureGates] = useState<WorkspaceFeatureGates>(
    selectedWs.feature_gates ?? {},
  );
  const [errors, setErrors] = useState<string>("");

  const patchWs = usePatchWorkspace();
  const createOperation = useCreateOperation();

  const { data: agentsModules } = useModules({
    workspaceId: selectedWs.id,
    kind: ModuleKind.AGENTS,
  });
  const agentsModule = agentsModules?.[0];
  // A deployed agents module enables Agent Builder by default; only an explicit
  // `false` on the `feature_flag_tktl_agents` flag disables it.
  const storedAgentBuilderFlag = agentsModule?.desiredSpec.config
    .feature_flag_tktl_agents as boolean | undefined;
  const storedAgentBuilderEnabled =
    agentsModule !== undefined && (storedAgentBuilderFlag ?? true);
  const [agentBuilderToggle, setAgentBuilderToggle] = useState<
    boolean | undefined
  >(undefined);
  const agentBuilderEnabled = agentBuilderToggle ?? storedAgentBuilderEnabled;

  const gatesChanged = (
    Object.keys(featureGates) as (keyof WorkspaceFeatureGates)[]
  ).some(
    (key) =>
      (featureGates[key] ?? false) !==
      (selectedWs.feature_gates?.[key] ?? false),
  );
  const agentBuilderChanged =
    agentsModule !== undefined &&
    agentBuilderEnabled !== storedAgentBuilderEnabled;
  const disableSubmit = !gatesChanged && !agentBuilderChanged;
  const isPending = patchWs.isPending || createOperation.isPending;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header>Modify Workspace Feature Gates</Modal.Header>
      <Modal.Content>
        {(
          Object.entries(featureGates) as [
            keyof WorkspaceFeatureGates,
            boolean,
          ][]
        ).map(([key, value]) => (
          <div key={key} className="my-2 flex items-center space-x-2">
            <Checkbox
              checked={value}
              // Workspace gates are monotonic: once enabled they can't be turned off.
              disabled={selectedWs.feature_gates?.[key]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFeatureGates((prev) => ({
                  ...prev,
                  [key]: event.target.checked,
                }));
              }}
            />
            <Label mb="mb-0">
              {getFeatureGateTitle(key)}
              {key in FEATURE_GATE_METADATA && (
                <span className="font-inter-normal-13px text-gray-500">
                  {" "}
                  ({key})
                </span>
              )}
            </Label>
          </div>
        ))}
        <div className="my-2 flex items-center space-x-2">
          <Checkbox
            checked={agentBuilderEnabled}
            disabled={agentsModule === undefined}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAgentBuilderToggle(event.target.checked);
            }}
          />
          <Label mb="mb-0">
            Agent Builder
            <span className="font-inter-normal-13px text-gray-500">
              {" "}
              (agents module)
            </span>
          </Label>
        </div>
        {agentsModule === undefined && (
          <Callout variant="info">
            The agents module is not deployed for this workspace, so Agent
            Builder can't be toggled here. Enable the module from the Modules
            tab first.
          </Callout>
        )}
        {featureGates.cm_copilot && !selectedWs.feature_gates?.cm_copilot && (
          <Callout variant="info">
            After enabling CM Copilot, go to the "Model quotas" and enable the
            "Case Copilot" feature.
          </Callout>
        )}
        <ErrorHint>{errors}</ErrorHint>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="submit"
            disabled={disableSubmit}
            loading={isPending}
            variant="primary"
            onClick={async () => {
              try {
                if (gatesChanged) {
                  const workspace = selectedWs;
                  workspace.feature_gates = featureGates;
                  await patchWs.mutateAsync(workspace);
                }
                if (agentBuilderChanged && agentsModule !== undefined) {
                  await createOperation.mutateAsync({
                    data: {
                      type: "module_operations",
                      attributes: {
                        patch: {
                          config: {
                            feature_flag_tktl_agents: agentBuilderEnabled,
                          },
                        },
                      },
                      relationships: {
                        module: {
                          data: { type: "modules", id: agentsModule.id },
                        },
                      },
                    },
                  });
                }
                onClose();
              } catch (e) {
                if (isAxiosError(e)) {
                  setErrors(
                    JSON.stringify(e.response?.data?.detail) ?? e.message,
                  );
                } else {
                  setErrors(JSON.stringify(e));
                }
              }
            }}
          >
            Submit
          </Button>
        }
      />
    </Modal>
  );
};
